import {
    DEFAULT_FPSR_BACKGROUND,
    fpsrBackgroundKey,
} from '@/lib/fpsr';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/ui';
import type { RenderBackground } from '@rickyzhangca/fpsr';
import { useFormContext } from 'react-hook-form';
import { ICreateBlueprintFormValues } from '../BlueprintForm';

type FpsrBackgroundOption = {
  label: string;
  value: RenderBackground;
};

/** Matches fpsr.fprints.xyz Background style options. */
const optionsBasic: FpsrBackgroundOption[] = [
  { label: 'Auto', value: { type: 'auto' } },
  { label: 'Checkerboard', value: { type: 'checkerboard' } },
];

const optionsTerrain: FpsrBackgroundOption[] = [
  { label: 'Dirt', value: { type: 'terrain', name: 'dirt' } },
  { label: 'Water', value: { type: 'terrain', name: 'water' } },
  { label: 'Vulcanus', value: { type: 'terrain', name: 'vulcanus' } },
  { label: 'Gleba', value: { type: 'terrain', name: 'gleba' } },
  { label: 'Fulgora', value: { type: 'terrain', name: 'fulgora' } },
  { label: 'Aquilo', value: { type: 'terrain', name: 'aquilo' } },
];

const optionsSpace: FpsrBackgroundOption[] = [
  { label: 'Space', value: { type: 'space' } },
  {
    label: 'Nauvis orbit',
    value: { type: 'space', planet: true, planetName: 'nauvis' },
  },
  {
    label: 'Vulcanus orbit',
    value: { type: 'space', planet: true, planetName: 'vulcanus' },
  },
  {
    label: 'Fulgora orbit',
    value: { type: 'space', planet: true, planetName: 'fulgora' },
  },
  {
    label: 'Gleba orbit',
    value: { type: 'space', planet: true, planetName: 'gleba' },
  },
  {
    label: 'Aquilo orbit',
    value: { type: 'space', planet: true, planetName: 'aquilo' },
  },
];

const allOptions = [...optionsBasic, ...optionsTerrain, ...optionsSpace];

function labelFor(bg: RenderBackground | undefined): string {
  const key = fpsrBackgroundKey(bg ?? DEFAULT_FPSR_BACKGROUND);
  return (
    allOptions.find(o => fpsrBackgroundKey(o.value) === key)?.label ??
    'Choose background'
  );
}

type ImageFpsrBackgroundProps = {
  onChange?: (background: RenderBackground) => void;
};

export const ImageFpsrBackground = ({ onChange }: ImageFpsrBackgroundProps) => {
  const form = useFormContext<ICreateBlueprintFormValues>();
  const fpsrBackground = form.watch('fpsr_background');

  const select = (value: RenderBackground) => {
    form.setValue('fpsr_background', value);
    onChange?.(value);
  };

  return (
    <div className="flex items-center justify-end gap-3">
      <p className="text-steel-300">Background</p>
      <DropdownMenu>
        <DropdownMenuTrigger>{labelFor(fpsrBackground)}</DropdownMenuTrigger>
        <DropdownMenuContent>
          {optionsBasic.map(option => (
            <DropdownMenuItem
              key={fpsrBackgroundKey(option.value)}
              onClick={() => select(option.value)}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {optionsTerrain.map(option => (
            <DropdownMenuItem
              key={fpsrBackgroundKey(option.value)}
              onClick={() => select(option.value)}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {optionsSpace.map(option => (
            <DropdownMenuItem
              key={fpsrBackgroundKey(option.value)}
              onClick={() => select(option.value)}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
